
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TeacherPaymentProcessor } from '@/components/teachers/TeacherPaymentProcessor';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useTeacherDetails } from '@/hooks/useTeacherDetails';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function TeacherPaymentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { teacher, isLoading, updateTeacher } = useTeacherDetails(id);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    // Check if a payment for the current month exists
    if (teacher) {
      const paymentsKey = `teacher_payments_${teacher.id}`;
      const existingPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
      
      const currentMonth = format(new Date(), 'MMMM yyyy');
      
      // Check if there's a payment for the current month
      const currentMonthPayment = existingPayments.find((p: any) => 
        p.paymentPeriod === currentMonth || 
        (p.date && format(new Date(p.date), 'MMMM yyyy') === currentMonth)
      );
      
      setPaymentProcessed(!!currentMonthPayment);
    }
  }, [teacher]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Helmet>
          <title>Teacher Not Found - NALC Student Program & Fee Tracker</title>
        </Helmet>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Teacher Not Found</h2>
          <Button onClick={() => navigate('/teachers')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Teachers
          </Button>
        </div>
      </div>
    );
  }

  const handlePaymentComplete = async () => {
    if (isProcessingPayment) {
      toast.info('Payment is already being processed...');
      return;
    }

    setIsProcessingPayment(true);
    
    try {
      console.log('Processing teacher payment completion...');
      toast.info('Processing payment...', { duration: 2000 });
      
      // Update the teacher's payment status
      if (teacher) {
        const updatedTeacher = {
          ...teacher,
          paymentStatus: 'Paid', // Update to paid
          updatedAt: new Date().toISOString()
        };
        
        await updateTeacher(updatedTeacher);
        setPaymentProcessed(true);
        
        console.log('Teacher payment status updated successfully');
        toast.success(`Payment processed successfully for ${teacher.firstName} ${teacher.lastName}`);
        
        // Navigate back to teacher details after a short delay
        setTimeout(() => {
          navigate(`/teachers/${id}`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating teacher payment status:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{`Payment for ${teacher.firstName} ${teacher.lastName}`} - NALC Student Program & Fee Tracker</title>
      </Helmet>
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Process Payment</h1>
        <Button variant="outline" className="mb-6" onClick={() => navigate(`/teachers/${id}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Teacher Details
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Teacher Information</h2>
            <div className="space-y-3">
              <p>Payment details for {teacher.firstName} {teacher.lastName}</p>
              <div>
                <span className="text-sm text-muted-foreground block">Position</span>
                <span className="font-medium">{teacher.position || 'Not specified'}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block">Department</span>
                <span className="font-medium">
                  {teacher.department || 
                   (teacher.departments && teacher.departments.length > 0 ? 
                    (typeof teacher.departments[0] === 'string' ? 
                     teacher.departments[0] : 
                     teacher.departments[0]?.name) : 
                    'Not specified')}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block">Fee Amount</span>
                <span className="font-medium">{teacher.salary || 0} AFN</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block">Status</span>
                <span className={`font-medium ${paymentProcessed ? 'text-green-500' : 'text-orange-500'}`}>
                  {isProcessingPayment ? 'Processing...' : (paymentProcessed ? 'Paid' : 'Pending Payment')}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block">Payment Period</span>
                <span className="font-medium">{format(new Date(), 'MMMM yyyy')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <TeacherPaymentProcessor
            teacherId={teacher.id}
            teacherName={`${teacher.firstName} ${teacher.lastName}`}
            salary={Number(teacher.salary) || 0}
            onPaymentComplete={handlePaymentComplete}
          />
        </div>
      </div>
    </div>
  );
}
